/**
 * CoffrePage — the cash-desk back-office: the X-report, the Z-freeze close + audited reopen, the
 * CaisseMouvement ledger (+ cash-out guard), and the closed-session corrections.
 *
 * Session resolution: it prefers the live OPEN session (00a-core GET /caisse/sessions/courante) but
 * also accepts a manually-entered session id so a supervisor can pull a closed session to reopen it
 * or post a correction. The 4 sub-surfaces live in tabs; each owns its own inline states.
 *
 * 5-state DoD (page level): sans-droit (ProtectedRoute on finance.caisse.read at the route + a
 * ForbiddenState fallback here), hors-ligne (OfflineState), chargement/erreur/vide (the courante
 * query → manual-load CTA). The per-action gating is via useCan on every mutating control.
 */
import { useState } from "react";
import {
  Badge,
  Button,
  Card,
  FormField,
  ForbiddenState,
  Input,
  LoadingState,
  OfflineState,
  Tabs,
} from "@/ui";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { token } from "@/design/theme";
import { FINANCE_CAPS, useCurrentSession, useLabId } from "./api";
import { XReportPanel } from "./components/XReportPanel";
import { ZFreezePanel } from "./components/ZFreezePanel";
import { MouvementsPanel } from "./components/MouvementsPanel";
import { CorrectionsPanel } from "./components/CorrectionsPanel";

export function CoffrePage() {
  const { t } = useT();
  const labId = useLabId();
  const online = useUiStore((s) => s.online);
  const canRead = useCan(FINANCE_CAPS.caisseRead);

  const [manualId, setManualId] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tab, setTab] = useState("xreport");
  // A monotonically-bumped key to force the X-report/corrections to refetch after a write.
  const [refreshNonce, setRefreshNonce] = useState(0);

  const sessionQuery = useCurrentSession(labId, canRead && online);
  const courante = sessionQuery.data ?? null;

  // The session we operate on: an explicit manual selection wins, else the live open session.
  const activeId = selectedId ?? (courante ? String(courante.id) : null);

  const header = (
    <header style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-1)" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: "var(--nv-s-3)" }}>
        <h1 style={{ fontSize: "var(--nv-fs-h1)", margin: 0 }}>{t("finance.coffre.title")}</h1>
        {activeId && (
          <Badge tone="info">
            {t("finance.06.sessionId")} <span className="nv-mono nv-ltr-island">{activeId}</span>
          </Badge>
        )}
      </div>
      <p style={{ margin: 0, color: token.slate }}>{t("finance.coffre.subtitle")}</p>
    </header>
  );

  if (!canRead) {
    return <ForbiddenState requiredCapabilities={[FINANCE_CAPS.caisseRead]} />;
  }
  if (!online) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
        {header}
        <OfflineState onRetry={() => void sessionQuery.refetch()} cached={!!courante} />
      </div>
    );
  }
  if (sessionQuery.isLoading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
        {header}
        <LoadingState variant="table" rows={4} />
      </div>
    );
  }

  const manualLoader = (
    <Card title={t("finance.coffre.manualLoad")}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (manualId.trim()) {
            setSelectedId(manualId.trim());
            setRefreshNonce((n) => n + 1);
          }
        }}
        noValidate
      >
        <div style={{ display: "flex", alignItems: "flex-end", gap: "var(--nv-s-3)", flexWrap: "wrap" }}>
          <FormField label={t("finance.06.sessionId")} hint={t("finance.06.sessionId.hint")}>
            {(p) => (
              <Input
                {...p}
                mono
                className="nv-ltr-island"
                inputMode="numeric"
                autoComplete="off"
                value={manualId}
                placeholder="123"
                style={{ maxWidth: 220 }}
                onChange={(e) => setManualId(e.target.value)}
              />
            )}
          </FormField>
          <Button type="submit" disabled={!manualId.trim()}>
            {t("finance.06.loadSession")}
          </Button>
          {selectedId && (
            <Button
              variant="ghost"
              onClick={() => {
                setSelectedId(null);
                setManualId("");
              }}
            >
              {t("action.close")}
            </Button>
          )}
        </div>
      </form>
    </Card>
  );

  // vide — no active session at all (no open session AND none entered manually).
  if (!activeId) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
        {header}
        <Card title={t("finance.coffre.none.title")}>
          <p style={{ margin: "0 0 var(--nv-s-4)", color: token.slate }}>
            {t("finance.coffre.none.desc")}
          </p>
        </Card>
        {manualLoader}
      </div>
    );
  }

  const bump = () => setRefreshNonce((n) => n + 1);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      {header}
      {manualLoader}

      <Tabs
        aria-label={t("finance.coffre.title")}
        value={tab}
        onChange={setTab}
        items={[
          {
            id: "xreport",
            label: t("finance.coffre.tab.xreport"),
            content: (
              <XReportPanel
                key={`x-${activeId}-${refreshNonce}`}
                labId={labId}
                sessionId={activeId}
                enabled
              />
            ),
          },
          {
            id: "mouvements",
            label: t("finance.coffre.tab.mouvements"),
            content: <MouvementsPanel labId={labId} sessionId={activeId} onChanged={bump} />,
          },
          {
            id: "corrections",
            label: t("finance.coffre.tab.corrections"),
            content: (
              <CorrectionsPanel
                key={`c-${activeId}-${refreshNonce}`}
                labId={labId}
                sessionId={activeId}
                enabled
              />
            ),
          },
          {
            id: "fermeture",
            label: t("finance.coffre.tab.fermeture"),
            content: <ZFreezePanel labId={labId} sessionId={activeId} onChanged={bump} />,
          },
        ]}
      />
    </div>
  );
}
